import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import Provider from "../../../../enum/Provider.enum";

export type DisputeClient = {
  client_account: string;
  brokerage_base?: number;
  retention_base?: number;
  attraction_base?: number;
};

type Props = {
  clients: DisputeClient[];
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: { accounts: string[]; comment: string }) => void;
};

const formatMoney = (value: unknown) => {
  return Number(value ?? 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const ArmeniaActDisputeDrawer = ({
  clients,
  loading = false,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const provider = useSelector((state: RootState) => state.app.provider);
  const hintKey = [Provider.UAE, Provider.TFOS].includes(provider)
    ? "armeniaAct.dispute.hintAE"
    : "armeniaAct.dispute.hint";

  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [comment, setComment] = useState("");

  const toggleAccount = (account: string) => {
    setSelectedAccounts((prev) =>
      prev.includes(account)
        ? prev.filter((v) => v !== account)
        : [...prev, account],
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t(hintKey)}
      </Typography>

      {clients.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {clients.map((client) => (
            <FormControlLabel
              key={client.client_account}
              control={
                <Checkbox
                  checked={selectedAccounts.includes(client.client_account)}
                  onChange={() => toggleAccount(client.client_account)}
                />
              }
              label={
                <span>
                  <strong>{client.client_account}</strong>
                  {" — "}
                  {t("armeniaAct.dispute.brokerage")}:{" "}
                  {formatMoney(client.brokerage_base)},{" "}
                  {t("armeniaAct.dispute.retention")}:{" "}
                  {formatMoney(client.retention_base)},{" "}
                  {t("armeniaAct.dispute.attraction")}:{" "}
                  {formatMoney(client.attraction_base)}
                </span>
              }
            />
          ))}
        </Stack>
      )}

      <TextField
        label={t("armeniaAct.dispute.comment")}
        multiline
        minRows={4}
        fullWidth
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          color="warning"
          disabled={loading || !comment.trim()}
          onClick={() =>
            onSubmit({
              accounts: selectedAccounts,
              comment,
            })
          }
        >
          {t("armeniaAct.dispute.submit")}
        </Button>

        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
      </Stack>
    </Box>
  );
};

export default ArmeniaActDisputeDrawer;
